import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { UncontrolledTooltip } from 'reactstrap'
import startCase from 'lodash/startCase'
import { useHistory, Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RentalLedger } from '@homevest/types/ledger'
import { capabilities, ledger as LEDGER, payments as PAYMENTS } from '@homevest/utils'

import {
  Alert,
  AlertIcon,
  Badge,
  Button,
  Flex,
  HStack,
  Text,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  IconButton,
  Spinner,
  useToast
} from '@chakra-ui/react'
import { Icon } from '@chakra-ui/icon'
import {
  ArrowDownOnSquareIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassPlusIcon,
  PlusIcon,
  ReceiptRefundIcon
} from '@heroicons/react/24/outline'
import { formatMoney } from 'lib/numbers'
import {
  canManuallyCorrectLedgerItem,
  canScheduleRentalCharge,
  getLedgerItemsWithBalance,
  LedgerItemWithBalance
} from 'lib/ledger'
import { getAddressForRental, getCSVExportForRentalLedger } from 'lib/rentals'
import { hasCapability } from 'lib/admin-perms'
import CreateRentalLiabilityModal from './CreateRentalLiabilityModal'
import IssueCreditModal from './IssueCreditModal'
import IssuePaymentRefundModal from './IssuePaymentRefundModal'
import BuildiumResyncModal from './BuildiumResyncModal'
import LedgerDetailModal from './LedgerDetailModal'
import UtilityChargebackModal from './UtilityChargebackModal'
import OrphanedUtilityChargebackModal from './OrphanedUtilityChargebackModal'
import UtilityChargebackChooserModal from './UtilityChargebackChooserModal'
import { getUtilityChargebackAvailability } from './utilityChargebackAccess'
import { StoreState } from 'store'
import { RefundInfoType } from '../types'
import { DeleteButton } from './LedgerRow/DeleteButton'
import { EditButton } from './LedgerRow/EditButton'
import moment from 'moment'
import { useRentRollLedgerDataQuery } from 'graphql/generated'
import type { RentRollLedgerDataQuery } from 'graphql/generated'
import axios from 'lib/axios'
import { dates } from '@homevest/utils'
import { CSVLink } from 'react-csv'
import CashoutValidAlert from './CashoutValidAlert'
import { Loading } from 'ui'
import { useBuildiumSyncGracePeriod } from './useBuildiumSyncGracePeriod'
import ScheduleChargeModal from './ScheduleChargeModal'
import ScheduledChargesTable from './ScheduledChargesTable'

const {
  LEDGER_LIABILITY_CREATOR,
  LEDGER_RENT_CREDIT_ISSUER,
  LEDGER_VIEWER,
  PAYMENT_REFUNDER,
  LEDGER_SUPER_EDITOR,
  EVICTION_MANAGER,
  ORPHANED_UTILITY_CHARGEBACK_CREATOR
} = capabilities.CAPABILITY_TYPES

const { PAYMENT_LIFECYCLE, PAYMENT_STATUSES } = PAYMENTS

type UtilityChargebackFlow = 'closed' | 'chooser' | 'bill-backed' | 'orphaned'

const FAILED_STATUSES: string[] = [PAYMENT_STATUSES.FAILED, PAYMENT_STATUSES.NSF_FAILED]
const EVICTION_LINKED_LIABILITY_BLOCKED_MESSAGE = {
  deleted:
    'This liability is linked to an eviction and requires the eviction manager capability to be deleted.',
  edited:
    'This liability is linked to an eviction and requires the eviction manager capability to be edited.'
} as const

type RentRollLedgerRental = NonNullable<RentRollLedgerDataQuery['rentals_by_pk']>
type RentRollLedgerPayment = RentRollLedgerRental['payments'][number]
type BuildiumResyncEligibilityReason =
  | 'already_has_buildium_id'
  | 'before_cutoff'
  | 'missing_buildium_lease'
  | 'sync_disabled'
  | 'convenience_fee'
  | 'missing_buildium_gl_account'
  | 'unsupported_type'

type BuildiumResyncEligibilityEntry = {
  eligible: boolean
  reason: BuildiumResyncEligibilityReason | null
}

type BuildiumResyncEligibilityResponse = {
  can_resync: boolean
  liabilities: Record<string, BuildiumResyncEligibilityEntry>
  credits: Record<string, BuildiumResyncEligibilityEntry>
}

type BuildiumResyncResponse = {
  buildium_id: number
  source: 'matched_existing' | 'posted'
}

const BUILDIUM_RESYNC_ERROR_MESSAGES: Record<string, string> = {
  lock_busy: 'Another Buildium sync is already running for this row.',
  already_has_buildium_id: 'This row is already linked to Buildium.',
  before_cutoff: 'Only rows created on or after April 4, 2025 can be resynced.',
  missing_buildium_lease: 'This rental is missing a Buildium lease id.',
  sync_disabled: 'This charge is not configured to sync to Buildium.',
  convenience_fee: 'Convenience fees are imported from Buildium and cannot be resynced.',
  missing_buildium_gl_account: 'This row is missing a Buildium GL account mapping.',
  unsupported_type: 'This row type is not supported for Buildium resync.',
  ineligible: 'This row is not eligible for Buildium resync.',
  ambiguous_match: 'Buildium returned more than one exact match. Engineering needs to review it.',
  buildium_id_already_claimed: 'That Buildium transaction is already linked to another Hudson row.',
  update_race: 'Hudson could not save the Buildium id because the row changed. Refresh and retry.',
  buildium_failure: 'Buildium returned an error while resyncing this row.',
  books_locked: 'Buildium books are locked for this posting date.',
  not_found: 'This row could not be found.',
  forbidden: 'You do not have permission to resync Buildium IDs.'
}

const ledgerHeaderCellProps = {
  fontSize: 'xs',
  letterSpacing: 0,
  lineHeight: '1.25',
  px: 3,
  py: 3,
  verticalAlign: 'bottom',
  whiteSpace: 'nowrap'
} as const

const ledgerCellProps = {
  fontSize: 'sm',
  px: 3,
  py: 3,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap'
} as const

const getStatusColorScheme = (status: string) => {
  if (status === PAYMENT_STATUSES.REFUNDED) return 'orange'
  if (status === PAYMENT_STATUSES.SUCCEEDED) return 'green'
  if (FAILED_STATUSES.includes(status)) return 'red'
  if (status === PAYMENT_STATUSES.PROCESSING) return 'blue'
  if (status === PAYMENT_STATUSES.PENDING) return 'yellow'
  return 'gray'
}

const isRentRollLedgerPaymentReady = (payment: RentRollLedgerPayment) =>
  'available_on' in payment &&
  'category' in payment &&
  'created_at' in payment &&
  'initiated_at' in payment &&
  'lifecycle' in payment &&
  'note' in payment &&
  'payment_date' in payment &&
  'payment_method' in payment &&
  'refund_reason' in payment &&
  'stripe_charge_id' in payment &&
  'stripe_payment_intent_id' in payment &&
  'buildium_transaction' in payment &&
  !!payment.liability_type &&
  'name' in payment.liability_type &&
  'payment_category' in payment.liability_type

const isRentRollLedgerRentalReady = (
  rental: RentRollLedgerRental | null | undefined
): rental is RentRollLedgerRental =>
  !!rental &&
  Array.isArray(rental.payments) &&
  rental.payments.every(isRentRollLedgerPaymentReady) &&
  Array.isArray(rental.rental_liabilities) &&
  Array.isArray(rental.rental_credits) &&
  rental.rental_credits.every((credit) => Array.isArray(credit.payment_plan_offset_credits)) &&
  Array.isArray(rental.option_premium_transfers_received) &&
  Array.isArray(rental.rental_users) &&
  !!rental.property &&
  Array.isArray(rental.property.llc_properties) &&
  'most_recent_llc_property' in rental.property &&
  'portfolio_home' in rental &&
  'cashout' in rental &&
  (!rental.cashout || Array.isArray(rental.cashout.cashout_adjustments))

const fetchLedger = async (rentalId: string) => {
  return (
    await axios.get<RentalLedger>(
      `/admin/rentals/${rentalId}/ledger?filter_date=${dates.formatISODate(new Date())}`
    )
  ).data
}

const fetchBuildiumResyncEligibility = async (rentalId: string) => {
  return (
    await axios.get<BuildiumResyncEligibilityResponse>(
      `/admin/rentals/${rentalId}/buildium_resync_eligibility`
    )
  ).data
}

export default function Ledger({
  onMonetaryDataChange,
  requestPolicy = 'cache-and-network',
  rentalId
}: {
  onMonetaryDataChange?: () => void
  requestPolicy?: 'cache-and-network' | 'network-only'
  rentalId: string
}) {
  const [isCreateRentalLiabilityModalOpen, setIsCreateRentalLiabilityModalOpen] = useState(false)
  const [isScheduleChargeModalOpen, setIsScheduleChargeModalOpen] = useState(false)
  const [utilityChargebackFlow, setUtilityChargebackFlow] =
    useState<UtilityChargebackFlow>('closed')
  const [isLedgerDetailModalOpen, setIsLedgerDetailModalOpen] = useState(false)
  const [ledgerDetailModalData, setLedgerDetailModalData] = useState({})
  const [isIssueCreditModalOpen, setIsIssueCreditModalOpen] = useState(false)
  const admin = useSelector((state: StoreState) => state.admin)
  const [isRefundPaymentModalOpen, setIsRefundPaymentModalOpen] = useState(false)
  const [refundInfo, setRefundInfo] = useState<RefundInfoType>()
  const [ledger, setLedger] = useState<RentalLedger>()
  const [ledgerDetailModalHeader, setLedgerDetailModalHeader] = useState('')
  const [buildiumResyncEligibility, setBuildiumResyncEligibility] =
    useState<BuildiumResyncEligibilityResponse>()
  const [buildiumResyncLedgerItem, setBuildiumResyncLedgerItem] = useState<LedgerItemWithBalance>()
  const [buildiumResyncError, setBuildiumResyncError] = useState<string>()
  const [resyncingLedgerItemId, setResyncingLedgerItemId] = useState<string>()
  const history = useHistory()
  const toast = useToast()
  const ledgerQueryContext = useMemo(() => ({ suspense: false }), [])
  const [{ data, fetching, error }, refetchLedgerRental] = useRentRollLedgerDataQuery({
    variables: { rentalId },
    requestPolicy,
    context: ledgerQueryContext
  })
  const rentals_by_pk = data?.rentals_by_pk

  const refetchLedger = useCallback(async () => {
    setLedger(await fetchLedger(rentalId))
  }, [rentalId])

  const refetchBuildiumResyncEligibility = useCallback(async () => {
    try {
      setBuildiumResyncEligibility(await fetchBuildiumResyncEligibility(rentalId))
    } catch (error) {
      console.error('Unable to load Buildium resync eligibility', error)
      setBuildiumResyncEligibility(undefined)
    }
  }, [rentalId])

  const refreshLedgerData = useCallback(async () => {
    refetchLedgerRental({ requestPolicy: 'network-only' })
    onMonetaryDataChange?.()
    await Promise.all([
      refetchLedger().catch((error) => {
        console.error('Unable to refresh ledger totals', error)
      }),
      refetchBuildiumResyncEligibility()
    ])
  }, [onMonetaryDataChange, refetchBuildiumResyncEligibility, refetchLedger, refetchLedgerRental])

  useEffect(() => {
    let isCurrent = true

    fetchLedger(rentalId).then((nextLedger) => {
      if (isCurrent) {
        setLedger(nextLedger)
      }
    })
    fetchBuildiumResyncEligibility(rentalId)
      .then((nextEligibility) => {
        if (isCurrent) {
          setBuildiumResyncEligibility(nextEligibility)
        }
      })
      .catch((error) => {
        console.error('Unable to load Buildium resync eligibility', error)
      })

    return () => {
      isCurrent = false
    }
  }, [rentalId])

  const { isPending: isBuildiumSyncPending, trackCreatedIds: trackPendingBuildiumSyncIds } =
    useBuildiumSyncGracePeriod({ onGracePeriodEnd: refreshLedgerData, rentalId })

  const canViewPage = hasCapability(admin, LEDGER_VIEWER)
  const canIssueRentCredits = hasCapability(admin, LEDGER_RENT_CREDIT_ISSUER)
  const canCreateLiabilities = hasCapability(admin, LEDGER_LIABILITY_CREATOR)
  const canRefund = hasCapability(admin, PAYMENT_REFUNDER)
  const canEditLedger = hasCapability(admin, LEDGER_SUPER_EDITOR)
  const canCreateOrphanedChargebacks = hasCapability(admin, ORPHANED_UTILITY_CHARGEBACK_CREATOR)
  const canManageEvictionLinkedLiabilities = hasCapability(admin, EVICTION_MANAGER)
  const canEditEntry = (date: string) => {
    const today = new Date()
    const dateMoment = moment(date)
    return (
      canEditLedger &&
      (dateMoment.isSameOrAfter(today, 'day') ||
        (today.getMonth() === dateMoment.month() && today.getFullYear() === dateMoment.year()))
    )
  }

  const ledgerRental = isRentRollLedgerRentalReady(rentals_by_pk) ? rentals_by_pk : null
  const payments = ledgerRental?.payments ?? []
  const rentalLiabilities = ledgerRental?.rental_liabilities ?? []
  const rentalCredits = ledgerRental?.rental_credits ?? []
  const scheduledCharges = ledgerRental?.scheduled_rental_liabilities ?? []
  const cashoutAdjustments = ledgerRental?.cashout?.cashout_adjustments

  const isLedgerLocked = !!ledgerRental?.cashout?.adjustments_finalized_at
  const isTenantCashedOut = !!ledgerRental?.cashout?.cashed_out_at
  const canScheduleCharge = ledgerRental
    ? canScheduleRentalCharge({
        canCreateLiabilities,
        isLedgerLocked,
        rentalStatus: ledgerRental.status
      })
    : false

  const { ledgerDataWithBalances } = getLedgerItemsWithBalance(
    payments,
    rentalLiabilities,
    rentalCredits,
    cashoutAdjustments
  )
  const paymentPlanLiabilityIds = useMemo(
    () =>
      new Set(
        rentalLiabilities
          .filter((liability) => liability.payment_plan_liability)
          .map((liability) => liability.id)
      ),
    [rentalLiabilities]
  )
  const paymentPlanOffsetCreditIds = useMemo(
    () =>
      new Set(
        rentalCredits
          .filter((credit) => credit.payment_plan_offset_credits.length > 0)
          .map((credit) => credit.id)
      ),
    [rentalCredits]
  )
  const evictionLinkedLiabilityIds = useMemo(
    () => new Set(ledger?.evictionLinkedLiabilityIds ?? []),
    [ledger?.evictionLinkedLiabilityIds]
  )
  const getPaymentPlanBlockedLedgerMessage = (itemType: string, itemId: string) => {
    if (paymentPlanLiabilityIds.has(itemId)) {
      return `This liability is part of a payment plan and cannot be ${itemType}. Remove the payment plan first.`
    }

    if (itemType === 'edited' || itemType === 'deleted') {
      if (paymentPlanOffsetCreditIds.has(itemId)) {
        return `This credit is part of a payment plan and cannot be ${itemType}. Remove the payment plan first.`
      }
    }

    return null
  }
  const getEvictionBlockedLedgerMessage = (
    itemType: keyof typeof EVICTION_LINKED_LIABILITY_BLOCKED_MESSAGE,
    ledgerItemType: LedgerItemWithBalance['type'],
    itemId: string
  ) => {
    if (ledgerItemType !== 'Charge') {
      return null
    }

    if (canManageEvictionLinkedLiabilities || !evictionLinkedLiabilityIds.has(itemId)) {
      return null
    }

    return EVICTION_LINKED_LIABILITY_BLOCKED_MESSAGE[itemType]
  }
  const getBlockedLedgerMutationMessage = (
    itemType: keyof typeof EVICTION_LINKED_LIABILITY_BLOCKED_MESSAGE,
    ledgerItemType: LedgerItemWithBalance['type'],
    itemId: string
  ) =>
    getPaymentPlanBlockedLedgerMessage(itemType, itemId) ??
    getEvictionBlockedLedgerMessage(itemType, ledgerItemType, itemId)
  const getBuildiumResyncEligibilityEntry = (ledgerItem: LedgerItemWithBalance) => {
    if (ledgerItem.type === 'Charge') {
      return buildiumResyncEligibility?.liabilities[ledgerItem.id]
    }
    if (ledgerItem.type === LEDGER.CREDIT) {
      return buildiumResyncEligibility?.credits[ledgerItem.id]
    }
    return undefined
  }
  const canResyncBuildiumLedgerItem = (ledgerItem: LedgerItemWithBalance) => {
    const eligibility = getBuildiumResyncEligibilityEntry(ledgerItem)
    return (
      ledgerItem.buildiumId == null &&
      !!buildiumResyncEligibility?.can_resync &&
      !!eligibility?.eligible
    )
  }
  const getBuildiumResyncErrorMessage = (error: unknown) => {
    const responseData = (error as any)?.response?.data ?? error
    const code = responseData?.code
    const reason = responseData?.reason
    const rawResponseMessage =
      (typeof responseData === 'string' ? responseData : responseData?.message) ||
      (error as Error).message
    const responseMessage =
      typeof rawResponseMessage === 'string' ? rawResponseMessage.trim().slice(0, 1000) : undefined
    const embeddedCode = Object.keys(BUILDIUM_RESYNC_ERROR_MESSAGES).find((knownCode) =>
      responseMessage?.includes(knownCode)
    )
    return (
      (code === 'buildium_failure' && responseMessage) ||
      (code && BUILDIUM_RESYNC_ERROR_MESSAGES[code]) ||
      (reason && BUILDIUM_RESYNC_ERROR_MESSAGES[reason]) ||
      (embeddedCode && BUILDIUM_RESYNC_ERROR_MESSAGES[embeddedCode]) ||
      responseMessage ||
      'Unable to resync this Buildium ID.'
    )
  }
  const handleResyncBuildiumLedgerItem = async (ledgerItem: LedgerItemWithBalance) => {
    const endpoint =
      ledgerItem.type === 'Charge'
        ? `/admin/rental_liabilities/${ledgerItem.id}/resync_buildium`
        : `/admin/rental_credits/${ledgerItem.id}/resync_buildium`
    const ledgerItemLabel = ledgerItem.type === 'Charge' ? 'charge' : 'credit'

    try {
      setBuildiumResyncError(undefined)
      setResyncingLedgerItemId(ledgerItem.id)
      const { data: resyncResult } = await axios.post<BuildiumResyncResponse>(endpoint)

      toast({
        title:
          resyncResult.source === 'posted'
            ? `Created a Buildium ${ledgerItemLabel}`
            : `Linked an existing Buildium ${ledgerItemLabel}`,
        description:
          resyncResult.source === 'posted'
            ? `Buildium transaction ${resyncResult.buildium_id} was created and linked to this Hudson row.`
            : `Buildium transaction ${resyncResult.buildium_id} was matched and linked to this Hudson row.`,
        duration: 5000,
        isClosable: true,
        status: 'success'
      })

      await refreshLedgerData()
      return resyncResult
    } catch (error) {
      setBuildiumResyncError(getBuildiumResyncErrorMessage(error))
      return null
    } finally {
      setResyncingLedgerItemId(undefined)
    }
  }

  if (!canViewPage) {
    history.push('/review')
    return null
  }

  if (fetching && !ledgerRental) {
    return <Loading />
  }

  if (error && !ledgerRental) {
    return (
      <Alert status='error'>
        <AlertIcon />
        Unable to load ledger data.
      </Alert>
    )
  }

  if (!ledgerRental) {
    return null
  }

  const portfolioHomeId = ledgerRental.portfolio_home?.id
  const utilityChargebackAvailability = getUtilityChargebackAvailability({
    canCreateLiabilities,
    canCreateOrphanedChargebacks,
    canEditLedger,
    isLedgerLocked,
    portfolioHomeId
  })
  const canCreateAnyUtilityChargeback =
    utilityChargebackAvailability.billBacked || utilityChargebackAvailability.orphaned

  const handleOpenUtilityChargeback = () => {
    if (utilityChargebackAvailability.billBacked && utilityChargebackAvailability.orphaned) {
      setUtilityChargebackFlow('chooser')
      return
    }

    setUtilityChargebackFlow(utilityChargebackAvailability.billBacked ? 'bill-backed' : 'orphaned')
  }

  const address = getAddressForRental(ledgerRental)
  const finalStatementDate = ledger?.finalStatement?.date

  const processingTotal = ledgerRental.payments
    .filter((payment) => payment.status === 'processing')
    .reduce((total, payment) => total + payment.amount, 0)
  const investmentTransfer = ledgerRental.option_premium_transfers_received[0]?.amount
  const ledgerActionButtons = [
    !isLedgerLocked && canCreateLiabilities ? (
      <Button
        key='liability'
        colorScheme='teal'
        leftIcon={<Icon as={PlusIcon} />}
        onClick={() => setIsCreateRentalLiabilityModalOpen(true)}
        size='sm'
      >
        Liability
      </Button>
    ) : null,
    canScheduleCharge ? (
      <Button
        key='scheduled-liability'
        colorScheme='teal'
        leftIcon={<Icon as={PlusIcon} />}
        onClick={() => setIsScheduleChargeModalOpen(true)}
        size='sm'
        variant='outline'
      >
        Schedule charge
      </Button>
    ) : null,
    !isLedgerLocked && canIssueRentCredits ? (
      <Button
        key='credit'
        colorScheme='teal'
        leftIcon={<Icon as={PlusIcon} />}
        onClick={() => setIsIssueCreditModalOpen(true)}
        size='sm'
      >
        Credit
      </Button>
    ) : null,
    canCreateAnyUtilityChargeback ? (
      <Button
        key='utility-chargeback'
        colorScheme='teal'
        leftIcon={<Icon as={PlusIcon} />}
        onClick={handleOpenUtilityChargeback}
        size='sm'
      >
        Utility chargeback
      </Button>
    ) : null,
    ledger ? (
      <Button key='ledger-export' colorScheme='teal' size='sm' variant='outline'>
        <CSVLink
          data={getCSVExportForRentalLedger(ledgerRental, ledger)}
          filename={`${address}-ledger-${new Date().getTime()}.csv`}
        >
          <HStack spacing={1}>
            <Icon as={ArrowDownOnSquareIcon} />
            <span>Ledger</span>
          </HStack>
        </CSVLink>
      </Button>
    ) : null
  ].filter((button): button is React.ReactElement => !!button)

  const handleLedgerMutationSuccess = async (createdLedgerItemIds?: string[]) => {
    trackPendingBuildiumSyncIds(createdLedgerItemIds)
    await refreshLedgerData()
  }

  const handleRefundSuccess = () => {
    setIsRefundPaymentModalOpen(false)
    toast({
      title:
        refundInfo?.paymentSource === 'buildium' ? 'Refund recorded in Hudson' : 'Payment refunded',
      description:
        refundInfo?.paymentSource === 'buildium'
          ? 'The payment now shows as refunded in Hudson.'
          : 'The refund was issued and the payment now shows as refunded.',
      duration: 5000,
      isClosable: true,
      status: 'success'
    })

    Promise.resolve(refreshLedgerData()).catch((error) => {
      console.error('Unable to refresh ledger after issuing payment refund', error)
    })
  }

  const handleCloseBuildiumResyncModal = () => {
    if (!resyncingLedgerItemId) {
      setBuildiumResyncLedgerItem(undefined)
      setBuildiumResyncError(undefined)
    }
  }

  const handleConfirmBuildiumResync = async () => {
    if (!buildiumResyncLedgerItem) {
      return
    }

    const resyncResult = await handleResyncBuildiumLedgerItem(buildiumResyncLedgerItem)
    if (resyncResult) {
      setBuildiumResyncLedgerItem(undefined)
    }
  }

  return (
    <>
      <Flex align='flex-start' gap={4} justify='space-between' wrap='wrap'>
        <TableContainer flex='0 1 auto'>
          <Table size='sm'>
            <Tbody>
              <Tr>
                <Td>
                  <span className='font-bold'>Outstanding Balance</span> (as of today)
                </Td>
                <Td>
                  <span className='font-bold'>{formatMoney(ledger?.balance, 2, '$')}</span>
                  {processingTotal > 0 && ` (+${formatMoney(processingTotal, 2, '$')} processing)`}
                </Td>
              </Tr>
              {investmentTransfer && (
                <Tr className='font-bold'>
                  <Td>Investment Transfer</Td>
                  <Td>{formatMoney(investmentTransfer, 2, '$')}</Td>
                </Tr>
              )}
              {ledger?.finalStatement && (
                <Tr>
                  <Td>
                    <span className='font-bold'>Wallet Value</span>
                    {finalStatementDate
                      ? ` (as of ${format(new Date(finalStatementDate), 'MMM yyyy')})`
                      : ''}
                  </Td>
                  <Td className='font-bold'>
                    {formatMoney(Number(ledger?.finalStatement?.ending_balance), 2, '$')}
                  </Td>
                </Tr>
              )}
              {isTenantCashedOut && (
                <Tr className='font-bold'>
                  <Td>Cashout Amount</Td>
                  <Td>{formatMoney(ledgerRental.cashout?.paid_out_amount, 2, '$')}</Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
        <Flex gap={2} justify='flex-end' ml='auto' wrap='wrap'>
          {ledgerActionButtons}
        </Flex>
      </Flex>
      {ledgerRental.cashout && ledger && <CashoutValidAlert ledger={ledger} />}
      <ScheduledChargesTable
        canCancel={canEditLedger && !isLedgerLocked}
        canResolve={canEditLedger}
        onChange={refreshLedgerData}
        schedules={scheduledCharges}
      />
      <TableContainer
        border='1px solid'
        borderColor='gray.200'
        borderRadius='sm'
        maxH='60vh'
        mt={4}
        overflowX='auto'
        overflowY='auto'
        sx={{
          '@media screen and (min-width: 1280px)': {
            maxHeight: 'none',
            overflowY: 'visible'
          }
        }}
      >
        <Table minW='770px' size='sm' style={{ tableLayout: 'fixed' }} variant='simple' w='full'>
          <Thead bg='gray.50' position='sticky' top={0} zIndex={1}>
            <Tr>
              <Th {...ledgerHeaderCellProps} w='105px'>
                Date
              </Th>
              <Th {...ledgerHeaderCellProps} textAlign='right' w='95px'>
                Amount
              </Th>
              <Th {...ledgerHeaderCellProps} w='120px'>
                Transaction
              </Th>
              <Th {...ledgerHeaderCellProps} w='115px'>
                Paid By
              </Th>
              <Th {...ledgerHeaderCellProps} w='95px'>
                Status
              </Th>
              <Th {...ledgerHeaderCellProps} textAlign='right' w='100px'>
                Balance
              </Th>
              <Th {...ledgerHeaderCellProps} textAlign='right' w='140px'>
                Actions
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {ledgerDataWithBalances.map((ledgerItem: LedgerItemWithBalance) => {
              const {
                id,
                user,
                amount,
                availableOn,
                balance,
                date,
                paymentMethod,
                liabilityLabel,
                type,
                status,
                lifecycle,
                initiatedAt,
                note,
                refundReason,
                liabilityTypeId,
                creditType,
                stripeId,
                buildiumId,
                paymentSource
              } = ledgerItem
              const typeLink =
                buildiumId && ledgerRental.buildium_lease_id
                  ? `https://upandup.managebuilding.com/manager/app/rentroll/${ledgerRental.buildium_lease_id}/financials/ledger?isByAccountView=0&isByDateView=1#_journalId_${buildiumId}`
                  : type === LEDGER.PAYMENT && stripeId
                  ? `https://dashboard.stripe.com/payments/${stripeId}`
                  : null
              const canResyncBuildium = canResyncBuildiumLedgerItem(ledgerItem)
              const isBuildiumSyncInGracePeriod = canResyncBuildium && isBuildiumSyncPending(id)
              const canManuallyCorrect = canManuallyCorrectLedgerItem(ledgerItem)

              return (
                <Tr
                  key={id}
                  className={[
                    FAILED_STATUSES.includes(status) ? 'alert-danger' : '',
                    type === LEDGER.ADJUSTMENT ? 'alert-warning' : ''
                  ].join(' ')}
                  style={{
                    fontStyle: moment(date).isAfter(moment()) ? 'italic' : ''
                  }}
                  _hover={{ bg: 'gray.50' }}
                >
                  <Td {...ledgerCellProps}>
                    {date}
                    {availableOn && (
                      <Text color='gray.600' fontSize='xs' lineHeight='short' mt={0.5}>
                        Available
                        <br />
                        {availableOn}
                      </Text>
                    )}
                  </Td>
                  <Td
                    {...ledgerCellProps}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                    textAlign='right'
                  >
                    {formatMoney(amount, 2, '$')}
                  </Td>
                  <Td {...ledgerCellProps} whiteSpace='normal'>
                    {typeLink ? (
                      <Link to={{ pathname: typeLink }} target='_blank'>
                        {startCase(type)}
                      </Link>
                    ) : (
                      startCase(type)
                    )}
                    <Text color='gray.600' fontSize='xs' mt={0.5}>
                      {startCase(liabilityLabel)}
                    </Text>
                  </Td>
                  <Td {...ledgerCellProps} whiteSpace='normal'>
                    {user ? (
                      <Link to={{ pathname: `/users/${user?.id}` }} target='_blank'>
                        {user?.first_name} {user?.last_name}
                      </Link>
                    ) : (
                      '-'
                    )}
                    {paymentMethod && (
                      <Text color='gray.600' fontSize='xs' mt={0.5}>
                        {paymentMethod.toUpperCase()}
                      </Text>
                    )}
                  </Td>
                  <Td {...ledgerCellProps}>
                    {status && (
                      <Badge
                        colorScheme={getStatusColorScheme(status)}
                        cursor={isInitiallyCleared(status, lifecycle) ? 'help' : undefined}
                        textDecoration={
                          isInitiallyCleared(status, lifecycle) ? 'underline' : 'none'
                        }
                        textTransform='none'
                        variant='subtle'
                        id={`status-ic-${id}`}
                      >
                        {startCase(
                          isInitiallyCleared(status, lifecycle) ? lifecycle ?? status : status
                        )}
                      </Badge>
                    )}
                    {isInitiallyCleared(status, lifecycle) && (
                      <UncontrolledTooltip placement='top' target={`status-ic-${id}`}>
                        <div>We have a high degree of certainty that this payment succeeded.</div>
                        <div style={{ whiteSpace: 'nowrap' }}>
                          Status:&nbsp;<b>{startCase(status)}</b>
                        </div>
                        {initiatedAt && (
                          <div>
                            Initiated:&nbsp;
                            <b>{new Date(initiatedAt).toString()}</b>
                          </div>
                        )}
                      </UncontrolledTooltip>
                    )}
                  </Td>
                  <Td
                    {...ledgerCellProps}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                    textAlign='right'
                  >
                    {formatMoney(balance, 2, '$')}
                  </Td>
                  <Td {...ledgerCellProps}>
                    <HStack justify='flex-end' minH='1.75rem' spacing={0.5}>
                      {isBuildiumSyncInGracePeriod && (
                        <Flex
                          align='center'
                          aria-label='Buildium sync in progress'
                          h='28px'
                          justify='center'
                          minW='28px'
                          role='status'
                          title='Buildium sync in progress'
                        >
                          <Spinner color='gray.500' size='xs' thickness='2px' />
                        </Flex>
                      )}
                      {canResyncBuildium && !isBuildiumSyncInGracePeriod && (
                        <IconButton
                          aria-label='Buildium sync required'
                          colorScheme='orange'
                          h='28px'
                          icon={<Icon as={ExclamationTriangleIcon} />}
                          isDisabled={!!resyncingLedgerItemId && resyncingLedgerItemId !== id}
                          isLoading={resyncingLedgerItemId === id}
                          minW='28px'
                          size='xs'
                          title='Buildium sync required — click to review'
                          variant='outline'
                          onClick={() => {
                            setBuildiumResyncError(undefined)
                            setBuildiumResyncLedgerItem(ledgerItem)
                          }}
                        />
                      )}
                      {(note || refundReason) && (
                        <IconButton
                          aria-label='See more ledger details'
                          h='28px'
                          icon={<Icon as={MagnifyingGlassPlusIcon} />}
                          minW='28px'
                          size='xs'
                          title='See more ledger details'
                          variant='ghost'
                          onClick={() => {
                            setIsLedgerDetailModalOpen(true)
                            setLedgerDetailModalData({
                              note,
                              refundReason
                            })
                            setLedgerDetailModalHeader(startCase(type))
                          }}
                        />
                      )}
                      {canRefund && type === LEDGER.PAYMENT && status === 'succeeded' && (
                        <IconButton
                          aria-label='Refund payment'
                          h='28px'
                          icon={<Icon as={ReceiptRefundIcon} />}
                          minW='28px'
                          size='xs'
                          title='Refund payment'
                          variant='ghost'
                          onClick={() => {
                            setRefundInfo({
                              amount,
                              address,
                              date,
                              paymentId: id,
                              paymentSource: paymentSource ?? 'stripe'
                            })
                            setIsRefundPaymentModalOpen(true)
                          }}
                        />
                      )}
                      {canEditEntry(date) && canManuallyCorrect && (
                        <EditButton
                          rentalId={rentalId}
                          ledgerItem={{
                            id,
                            user,
                            amount,
                            availableOn,
                            date,
                            paymentMethod,
                            liabilityLabel,
                            type,
                            status,
                            lifecycle,
                            initiatedAt,
                            note,
                            refundReason,
                            liabilityTypeId,
                            creditType
                          }}
                          blockedEditMessage={getBlockedLedgerMutationMessage('edited', type, id)}
                          onSuccess={refreshLedgerData}
                        />
                      )}
                      {canEditLedger && canManuallyCorrect && (
                        <DeleteButton
                          rentalId={rentalId}
                          ledgerItem={{
                            id,
                            amount,
                            type,
                            liabilityLabel,
                            date
                          }}
                          blockedDeleteMessage={getBlockedLedgerMutationMessage(
                            'deleted',
                            type,
                            id
                          )}
                          onSuccess={refreshLedgerData}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </TableContainer>
      <>
        {!isLedgerLocked && canCreateLiabilities && (
          <>
            <CreateRentalLiabilityModal
              rentalId={rentalId}
              occupancyDate={ledgerRental.occupancy_date}
              close={() => setIsCreateRentalLiabilityModalOpen(false)}
              isOpen={isCreateRentalLiabilityModalOpen}
              onSuccess={handleLedgerMutationSuccess}
            />
            {canScheduleCharge && (
              <ScheduleChargeModal
                finalLiabilityDate={ledgerRental.final_liability_date}
                isOpen={isScheduleChargeModalOpen}
                occupancyDate={ledgerRental.occupancy_date}
                rentalId={rentalId}
                onClose={() => setIsScheduleChargeModalOpen(false)}
                onSuccess={refreshLedgerData}
              />
            )}
          </>
        )}
        {utilityChargebackAvailability.billBacked && portfolioHomeId && (
          <React.Suspense fallback={null}>
            <UtilityChargebackModal
              finalLiabilityDate={ledgerRental.final_liability_date}
              rentalId={rentalId}
              occupancyDate={ledgerRental.occupancy_date}
              portfolioHomeId={portfolioHomeId}
              onSuccess={(createdLiabilityIds) => handleLedgerMutationSuccess(createdLiabilityIds)}
              onBack={
                utilityChargebackAvailability.orphaned
                  ? () => setUtilityChargebackFlow('chooser')
                  : undefined
              }
              onClose={() => setUtilityChargebackFlow('closed')}
              isOpen={utilityChargebackFlow === 'bill-backed'}
            />
          </React.Suspense>
        )}
        {utilityChargebackAvailability.orphaned && (
          <React.Suspense fallback={null}>
            <OrphanedUtilityChargebackModal
              finalLiabilityDate={ledgerRental.final_liability_date}
              rentalId={rentalId}
              occupancyDate={ledgerRental.occupancy_date}
              onAmbiguousOutcome={refreshLedgerData}
              onBack={
                utilityChargebackAvailability.billBacked
                  ? () => setUtilityChargebackFlow('chooser')
                  : undefined
              }
              onSuccess={(createdLiabilityIds) => handleLedgerMutationSuccess(createdLiabilityIds)}
              onClose={() => setUtilityChargebackFlow('closed')}
              isOpen={utilityChargebackFlow === 'orphaned'}
            />
          </React.Suspense>
        )}
        {utilityChargebackAvailability.billBacked && utilityChargebackAvailability.orphaned && (
          <UtilityChargebackChooserModal
            isOpen={utilityChargebackFlow === 'chooser'}
            onClose={() => setUtilityChargebackFlow('closed')}
            onChooseBillBacked={() => setUtilityChargebackFlow('bill-backed')}
            onChooseOrphaned={() => setUtilityChargebackFlow('orphaned')}
          />
        )}
        {canIssueRentCredits && (
          <IssueCreditModal
            rentalId={rentalId}
            availableTabs={['oneTime']}
            close={() => setIsIssueCreditModalOpen(false)}
            isOpen={isIssueCreditModalOpen}
            onSuccess={handleLedgerMutationSuccess}
          />
        )}
        {canRefund && refundInfo && (
          <IssuePaymentRefundModal
            close={() => setIsRefundPaymentModalOpen(false)}
            isOpen={isRefundPaymentModalOpen}
            onSuccess={handleRefundSuccess}
            refundInfo={refundInfo}
          />
        )}
        <BuildiumResyncModal
          errorMessage={buildiumResyncError}
          isLoading={resyncingLedgerItemId === buildiumResyncLedgerItem?.id}
          isOpen={!!buildiumResyncLedgerItem}
          ledgerItem={buildiumResyncLedgerItem}
          onClose={handleCloseBuildiumResyncModal}
          onConfirm={handleConfirmBuildiumResync}
        />
        <LedgerDetailModal
          close={() => setIsLedgerDetailModalOpen(false)}
          isOpen={isLedgerDetailModalOpen}
          data={ledgerDetailModalData}
          header={ledgerDetailModalHeader}
        />
      </>
    </>
  )
}

const isInitiallyCleared = (status: string, lifecycle: string | null | undefined) =>
  [`${PAYMENT_STATUSES.PENDING}`, `${PAYMENT_STATUSES.PROCESSING}`].includes(status) &&
  lifecycle === PAYMENT_LIFECYCLE.INITIALLY_CLEARED
